import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { get, setWith } from 'lodash';
import classNames from 'classnames';
import DecodeService from '../../services/DecodeService';
import LoadingOverlay from '../../components/LoadingOverlay';

const MAKE_CLASS = 'accessory-make-checkbox';
const MODEL_CLASS = 'accessory-model-checkbox';
const TRIM_CLASS = 'accessory-trim-checkbox';

const ALL = 'all';

const getAllMakesOption = () => ({
  label: 'All Makes & Models',
  className: MAKE_CLASS,
  value: ALL,
});

const getAllModelsOption = (make) => ({
  label: 'All Models',
  className: MODEL_CLASS,
  value: `${make}.${ALL}`,
});

const getAllTrimsOption = (make, year, model) => ({
  label: 'All Trims',
  className: TRIM_CLASS,
  value: `${make}.${year}.${model}.${ALL}`,
});

const CarMappingTree = React.memo(
  ({ dealer, mapping, setMapping, yearRange }) => {
    const [tree, setTree] = useState({});
    const [fetching, setFetching] = useState(false);

    /**
     * Check if mapping object has a value
     */
    const isSelected = (value) => !!get(mapping, value);

    /**
     * Recursively update selection flag for nodes
     */
    const toggleSubtree = (node, selected) => {
      if (!node) return;
      node.selected = selected;
      (node.children || []).forEach((c) => toggleSubtree(c, selected));
    };

    /**
     * Build the final car mapping object from the tree.
     */
    const buildMappingFromTree = (node, newMapping) => {
      if (!node || !node.selected) {
        return newMapping;
      }

      if (!node.children) {
        setWith(newMapping, node.value, true, Object);

        // Complementary values to put into the mapping (trim model codes)
        (node.extraValues || []).forEach((v) =>
          setWith(newMapping, v, true, Object),
        );
      } else if (node.children[0].selected) {
        const value = node.value || '';
        setWith(newMapping, value ? `${value}.${ALL}` : ALL, true, Object);
      } else if (node.children.length === 1) {
        // If value has no children loaded yet - extract selected values from the previous mapping
        const prevValue = get(mapping, node.value);
        if (prevValue) {
          setWith(newMapping, node.value, prevValue, Object);
        }
      } else {
        node.children
          .slice(1)
          .forEach((c) => buildMappingFromTree(c, newMapping));
      }
      return newMapping;
    };

    /**
     * Update each node selection flag based on the value of its children.
     */
    const refreshTree = (node) => {
      if (!node) {
        return [false, false];
      }

      if (!node.children || node.children.length === 0) {
        return [node.selected, node.selected];
      }

      if (node.children.length === 1) {
        return [node.selected, node.children[0].selected];
      }

      const childrenSelection = node.children
        .slice(1)
        .map((c) => refreshTree(c));
      const isAllSelected = childrenSelection.every((c) => c[1]);
      const isNodeSelected =
        isAllSelected || childrenSelection.some((c) => c[0]);

      node.selected = isNodeSelected;
      node.children[0].selected = isAllSelected;
      return [isNodeSelected, isAllSelected];
    };

    const updateTreeFromMapping = (node) => {
      if (!node) return;

      const nodeValue = node.value || '';
      const allValue = nodeValue ? `${nodeValue}.${ALL}` : ALL;
      const isAllSelected = isSelected(allValue);
      node.selected = isAllSelected || isSelected(nodeValue);

      if (isAllSelected) {
        toggleSubtree(node, true);
      } else if (node.selected || !nodeValue) {
        const children = (node.children || []).slice(1);
        children.forEach((c) => updateTreeFromMapping(c));
      }
    };

    const refreshMapping = (currentTree) => {
      const newMapping = buildMappingFromTree(currentTree, {});
      setMapping(newMapping);
    };

    /**
     * Fetch makes and models to build the tree
     */
    // This function is bound to a special `this` value. Should probably refactor
    // to use parameters instead.
    /* eslint-disable react/no-this-in-sfc */
    const buildTree = async () => {
      try {
        setFetching(true);

        // Fetch all makes & models
        const brands = (dealer.brand || []).join(',');
        const cars = await DecodeService.getRecentModels(brands);

        // Group cars by make
        const carsByMake = cars.reduce((acc, car) => {
          acc[car.make] = acc[car.make] || [];
          acc[car.make].push(car);
          return acc;
        }, {});

        // Generate make options, first item is a special option to select all makes
        const makeOptions = [getAllMakesOption()];
        const allModelOptions = [];

        for (const make of Object.keys(carsByMake)) {
          // Generate model options for make, first item is a special option to select all models
          const modelOptions = [getAllModelsOption(make)];

          for (const car of carsByMake[make]) {
            const { year, model } = car;

            if (year >= yearRange[0] && year <= yearRange[1]) {
              modelOptions.push({
                label: `${year} ${model}`,
                value: `${make}.${year}.${model}`,
                className: MODEL_CLASS,
                // First item is a special option to select all trims
                children: [getAllTrimsOption(make, year, model)],
                expandable: true,

                async loadChildren(currentTree) {
                  // Load trims if needed
                  if (this.children.length <= 1) {
                    try {
                      this.fetching = true;
                      setTree({ ...currentTree });

                      const carsWithTrim = await DecodeService.getTrimsForModel(
                        {
                          dealerId: dealer.id,
                          year: car.year,
                          make: car.make,
                          model: car.model,
                        },
                      );

                      const trimOptions = carsWithTrim
                        .filter((c) => c && c.trim)
                        .map((c) => {
                          const value = `${make}.${year}.${model}.${c.trim}`;

                          // Also add all model codes name for a trim
                          const extraValues = (c.mfrModelCodes || [])
                            .map((modelCode) => modelCode.name)
                            .filter((name) => !!name)
                            .map((name) => `${make}.${year}.${model}.${name}`);

                          const selected =
                            this.children[0].selected ||
                            (this.selected && isSelected(value));
                          return {
                            value,
                            selected,
                            extraValues,
                            label: c.trim,
                            className: TRIM_CLASS,
                          };
                        });
                      this.children.push(...trimOptions);
                    } finally {
                      this.fetching = false;
                    }
                  }
                },
              });
            }
          }

          allModelOptions.push(...modelOptions);
          makeOptions.push({
            label: make,
            value: make,
            children: modelOptions,
            className: MAKE_CLASS,
            expandable: true,
            expanded: true,
          });
        }

        const newTree = { children: makeOptions };
        updateTreeFromMapping(newTree);
        refreshTree(newTree);
        setTree(newTree);
        refreshMapping(newTree);

        allModelOptions
          .filter((o) => o.selected && o.children && !o.children[0].selected)
          .forEach(async (o) => {
            await o.loadChildren.call(o, newTree);
            o.expanded = true;
            setTree({ ...newTree });
          });
      } finally {
        setFetching(false);
      }
    };
    /* eslint-enable react/no-this-in-sfc */

    useEffect(() => {
      buildTree();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [yearRange]);

    const renderCheckbox = (node, parent, selectAll) => (
      <LoadingOverlay active={node.fetching} size="small">
        <div className={node.className}>
          <i
            onClick={async (e) => {
              e.stopPropagation();

              // Load children when node is expanded
              if (node.loadChildren && !node.expanded) {
                await node.loadChildren(tree);
              }
              node.expanded = !node.expanded;
              setTree({ ...tree });
            }}
            className={classNames('glyphicon accessory-mapping-toggle-icon', {
              visible: node.expandable,
              'glyphicon-triangle-right': !node.expanded,
              'glyphicon-triangle-bottom': node.expanded,
            })}
          />
          <div
            className="accessory-checkbox-wrapper"
            onClick={() => {
              const selected = !node.selected;
              const subtree = selectAll ? parent : node;

              // Toggle nested nodes
              toggleSubtree(subtree, selected);

              // Refresh parent values in the tree
              refreshTree(tree);
              setTree({ ...tree });

              // Update mapping
              refreshMapping(tree);
            }}
          >
            <input
              type="checkbox"
              checked={node.selected || false}
              onChange={() => {}}
            />{' '}
            {node.label}
          </div>
        </div>
      </LoadingOverlay>
    );

    const renderNode = (node, parent, idx) => {
      if (!node) return null;
      const { children = [], expanded } = node;
      return (
        <div key={idx}>
          {parent && renderCheckbox(node, parent, parent && idx === 0)}
          {(expanded || !parent) &&
            children.map((child, i) => renderNode(child, node, i))}
        </div>
      );
    };

    return (
      <div>
        <label>Make / Model / Trim</label>
        <LoadingOverlay active={fetching}>
          <div className="accessory-mapping-tree">{renderNode(tree, null)}</div>
        </LoadingOverlay>
      </div>
    );
  },
);

CarMappingTree.propTypes = {
  dealer: PropTypes.object,
  mapping: PropTypes.object,
  setMapping: PropTypes.func,
  yearRange: PropTypes.array,
};

export default CarMappingTree;
